// src/components/SocialMediaMarketingPage.js
import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import Home from '../Home';

// --- Colors & Fonts ---
const COLORS = {
  primaryBlue: '#05023C',
  accentBlue: '#007bff',
  redAccent: '#FF4757',
  orangeAccent: '#FF6B35',
  purpleAccent: '#6C63FF',
  tealAccent: '#00D4AA',
  greyText: '#666666',
  lightGreyBg: '#F8F9FA',
  darkBg: '#05023C',
  white: '#FFFFFF',
  gradientPrimary: 'linear-gradient(135deg, #05023C 0%, #2D1B69 100%)',
  gradientAccent: 'linear-gradient(135deg, #FF4757 0%, #FF6B35 100%)',
};

const FONT_SIZES = {
  hero: 'clamp(2.5rem, 5vw, 4.5rem)',
  sectionTitle: 'clamp(1.8rem, 4vw, 2.5rem)',
  subTitle: 'clamp(1.1rem, 2vw, 1.4rem)',
  body: 'clamp(0.9rem, 1.5vw, 1rem)',
};

const SHADOWS = {
  soft: '0 4px 20px rgba(0, 0, 0, 0.08)',
  medium: '0 8px 30px rgba(0, 0, 0, 0.12)',
  large: '0 20px 60px rgba(0, 0, 0, 0.15)',
  glow: '0 0 30px rgba(255, 71, 87, 0.3)',
};

// --- Animations ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// --- Global Page Structure ---
export const PageContainer = styled.div`
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  color: ${COLORS.primaryBlue};
  line-height: 1.7;
  overflow-x: hidden;
`;

export const Section = styled.section`
  padding: clamp(3rem, 8vw, 6rem) clamp(1rem, 5vw, 10%);
  text-align: center;
  background-color: ${props => props.dark ? COLORS.darkBg : COLORS.white};
  position: relative;
`;

export const SectionTitle = styled.h2`
  font-size: ${FONT_SIZES.sectionTitle};
  font-weight: 800;
  margin-bottom: 1rem;
  color: ${props => props.dark ? COLORS.white : COLORS.primaryBlue};
  line-height: 1.2;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: ${COLORS.gradientAccent};
    margin: 1rem auto;
    border-radius: 2px;
  }
`;

// --- Super SMM Bar ---
export const SMMBar = styled.div`
  background: ${COLORS.gradientPrimary};
  padding: 3rem 0;
  text-align: center;
  color: ${COLORS.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(108, 99, 255, 0.1) 0%, transparent 50%);
  }
`;

export const SMMTitle = styled.h2`
  font-size: ${FONT_SIZES.subTitle};
  font-weight: 700;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  position: relative;
`;

export const PlatformList = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(1rem, 3vw, 3rem);
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

export const PlatformItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: ${FONT_SIZES.body};
  font-weight: 600;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  .icon {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    
    &.facebook { background: rgba(59, 89, 152, 0.2); border-color: rgba(59, 89, 152, 0.4); }
    &.instagram { background: rgba(228, 64, 95, 0.2); border-color: rgba(228, 64, 95, 0.4); }
    &.twitter { background: rgba(29, 161, 242, 0.2); border-color: rgba(29, 161, 242, 0.4); }
    &.pinterest { background: rgba(189, 8, 28, 0.2); border-color: rgba(189, 8, 28, 0.4); }
    &.linkedin { background: rgba(0, 119, 181, 0.2); border-color: rgba(0, 119, 181, 0.4); }
  }
`;

export const SMMBarText = styled.p`
  font-size: ${FONT_SIZES.body};
  max-width: 800px;
  margin: 1rem auto 2rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

export const RuleText = styled.p`
  font-style: italic;
  font-size: 1.2rem;
  color: ${COLORS.redAccent};
  font-weight: 700;
  text-shadow: 0 0 20px rgba(255, 71, 87, 0.5);
`;

// --- Header/Hero Section ---
export const HeaderSection = styled(Section)`
  padding-top: 2rem;
  background: ${COLORS.lightGreyBg};
  text-align: left;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 60%;
    height: 200%;
    background: radial-gradient(circle, rgba(108, 99, 255, 0.05) 0%, transparent 70%);
    z-index: 0;
  }
`;

export const HeroTitle = styled.h1`
  font-size: ${FONT_SIZES.hero};
  font-weight: 900;
  line-height: 1.1;
  margin-bottom: 2rem;
  color: ${COLORS.primaryBlue};
  position: relative;
`;

export const BrandName = styled.span`
  background: ${COLORS.gradientAccent};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: block;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${COLORS.gradientAccent};
    border-radius: 2px;
  }
`;

export const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 3rem;
  align-items: center;
  position: relative;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  p {
    font-size: ${FONT_SIZES.body};
    color: ${COLORS.greyText};
    line-height: 1.8;
    
    strong {
      font-weight: 700;
      color: ${COLORS.primaryBlue};
    }
  }
`;

export const HeroImage = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.gradientPrimary};
  border-radius: 20px;
  min-height: 300px;
  box-shadow: ${SHADOWS.large};
  position: relative;
  overflow: hidden;
  animation: ${float} 6s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }
  
  p {
    color: white;
    font-size: 1.2rem;
    font-weight: 600;
    z-index: 1;
  }
`;

export const IconContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  
  p {
    color: ${COLORS.greyText};
    font-size: 1.1rem;
  }
`;

// --- Why Opt Section ---
export const WhyOptGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

export const WhyOptItem = styled.div`
  padding: 2.5rem 1.5rem;
  text-align: center;
  border-radius: 20px;
  background: ${COLORS.white};
  box-shadow: ${SHADOWS.soft};
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${COLORS.gradientAccent};
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${SHADOWS.large};
  }
  
  ${IconContainer} {
    background: ${COLORS.gradientPrimary};
    color: ${COLORS.white};
    width: 80px;
    height: 80px;
    border-radius: 50%;
    margin: 0 auto 1.5rem;
    font-size: 2rem;
    box-shadow: ${SHADOWS.medium};
  }
`;

export const WhyOptTitle = styled.h3`
  font-size: ${FONT_SIZES.subTitle};
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${COLORS.primaryBlue};
`;

export const WhyOptDescription = styled.p`
  font-size: ${FONT_SIZES.body};
  color: ${COLORS.greyText};
  line-height: 1.7;
`;

// --- Our Approach Section ---
export const ApproachSection = styled(Section)`
  background-color: ${COLORS.white};
`;

export const ApproachItem = styled.div`
  display: flex;
  align-items: center;
  background: ${COLORS.white};
  border-radius: 20px;
  padding: 2.5rem;
  margin: 2rem auto;
  max-width: 900px;
  text-align: left;
  box-shadow: ${SHADOWS.medium};
  border-left: 5px solid ${COLORS.redAccent};
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;
  
  &:hover {
    transform: translateX(10px);
    box-shadow: ${SHADOWS.large};
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

export const ApproachIcon = styled.div`
  background: ${COLORS.gradientPrimary};
  color: ${COLORS.white};
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-right: 2rem;
  flex-shrink: 0;
  box-shadow: ${SHADOWS.medium};
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 1.5rem;
  }
`;

export const ApproachContent = styled.div`
  h3 {
    font-size: ${FONT_SIZES.subTitle};
    font-weight: 700;
    color: ${COLORS.primaryBlue};
    margin-bottom: 1rem;
  }
  p {
    font-size: ${FONT_SIZES.body};
    color: ${COLORS.greyText};
    margin: 0;
    line-height: 1.7;
  }
`;

// --- Superb Process Section ---
export const ProcessSection = styled(Section).attrs({ dark: true })`
  background: ${COLORS.gradientPrimary};
  color: ${COLORS.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%236C63FF' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
  }
`;

export const ProcessTitle = styled(SectionTitle).attrs({ as: 'h2' })`
  color: ${COLORS.white};
  text-align: center;
  margin-bottom: 1rem;
`;

export const ProcessSubtitle = styled.p`
  font-size: ${FONT_SIZES.subTitle};
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 3rem;
  font-weight: 500;
`;

export const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  text-align: left;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

export const ProcessImageContainer = styled.div`
  position: relative;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 400px;
`;

export const ProcessStepList = styled.div`
  position: relative;
`;

export const StepItem = styled.div`
  margin-bottom: 2.5rem;
  position: relative;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(10px);
  }
  
  &::before {
    content: '${props => props.number}';
    position: absolute;
    top: -15px;
    left: -15px;
    width: 40px;
    height: 40px;
    background: ${COLORS.gradientAccent};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 1.2rem;
    box-shadow: ${SHADOWS.glow};
  }
  
  h3 {
    color: ${COLORS.redAccent};
    font-size: ${FONT_SIZES.subTitle};
    margin-bottom: 0.5rem;
    font-weight: 700;
  }
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: ${FONT_SIZES.body};
    line-height: 1.7;
    margin: 0;
  }
`;

// --- Pillars Section ---
export const PillarsSection = styled(Section).attrs({ dark: true })`
  color: ${COLORS.white};
  text-align: left;
  background: ${COLORS.gradientPrimary};
  position: relative;
`;

export const PillarsTitle = styled(SectionTitle).attrs({ as: 'h2' })`
  color: ${COLORS.white};
  text-align: center;
  margin-bottom: 3rem;
`;

export const PillarsNav = styled.nav`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const PillarNavItem = styled.button`
  background: ${props => props.active ? COLORS.gradientAccent : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  padding: 1rem 2rem;
  color: #000000;
  font-size: ${FONT_SIZES.body};
  font-weight: ${props => props.active ? '700' : '500'};
  cursor: pointer;
  border-radius: 50px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  
  &:hover {
    background: ${props => props.active ? COLORS.gradientAccent : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-2px);
  }
`;

export const PillarContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 4rem;
  align-items: center;
  padding: 3rem 0;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

export const PillarText = styled.div`
  p {
    font-size: ${FONT_SIZES.body};
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.8;
    margin-bottom: 1.5rem;
    
    strong {
      color: ${COLORS.white};
      font-weight: 700;
    }
  }
`;

export const PillarImage = styled.div`
  background: ${COLORS.gradientAccent};
  border-radius: 20px;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${SHADOWS.large};
  position: relative;
  overflow: hidden;
  animation: ${pulse} 4s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }
  
  p {
    color: ${COLORS.white};
    font-size: 1.3rem;
    font-weight: 700;
    z-index: 1;
  }
`;

// --- CTA Section ---
export const CtaSection = styled(Section)`
  background: ${COLORS.gradientAccent};
  padding: 5rem clamp(1rem, 5vw, 10%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  }
`;

export const CtaTitle = styled.h2`
  font-size: ${FONT_SIZES.sectionTitle};
  font-weight: 800;
  color: ${COLORS.white};
  margin-bottom: 1rem;
  position: relative;
`;

export const CtaSubtitle = styled.p`
  font-size: ${FONT_SIZES.subTitle};
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 3rem;
  position: relative;
`;

export const CtaForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormElement = css`
  padding: 1rem 1.5rem;
  font-size: ${FONT_SIZES.body};
  border-radius: 50px;
  border: none;
  outline: none;
  transition: all 0.3s ease;
`;

export const FormInput = styled.input`
  ${FormElement}
  background-color: rgba(255, 255, 255, 0.95);
  border: 2px solid transparent;
  
  &:focus {
    background-color: ${COLORS.white};
    border-color: ${COLORS.primaryBlue};
    box-shadow: 0 0 0 3px rgba(5, 2, 60, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

export const FormButton = styled.button`
  ${FormElement}
  background: ${COLORS.primaryBlue};
  color: ${COLORS.white};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #000020;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

// --- FAQ Section ---
export const FAQSection = styled(Section)`
  background-color: ${COLORS.lightGreyBg};
`;

export const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: left;
`;

export const FAQItem = styled.div`
  border-radius: 15px;
  margin-bottom: 1.5rem;
  overflow: hidden;
  background-color: ${COLORS.white};
  box-shadow: ${SHADOWS.soft};
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: ${SHADOWS.medium};
  }
`;

export const FAQQuestion = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1.5rem 2rem;
  background-color: ${COLORS.white};
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: ${FONT_SIZES.subTitle};
  font-weight: 600;
  color: ${COLORS.primaryBlue};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

export const ToggleIcon = styled.span`
  font-size: 1.2rem;
  transform: rotate(${props => props.active ? '180deg' : '0'});
  transition: transform 0.3s ease;
  color: ${COLORS.redAccent};
`;

export const FAQAnswer = styled.div`
  padding: ${props => props.active ? '0 2rem 1.5rem 2rem' : '0 2rem'};
  max-height: ${props => props.active ? '500px' : '0'};
  opacity: ${props => props.active ? '1' : '0'};
  transition: all 0.4s ease-in-out;
  overflow: hidden;
  
  p {
    color: ${COLORS.greyText};
    line-height: 1.7;
    margin: 0;
  }
`;

// =========================================================
// 2. DATA (same as before)
// =========================================================
const pillarData = [
  {
    title: 'Solid storyline',
    content: (
      <>
        <p>The story of <strong>SRK Software</strong> starts with a bright idea and a group of tech enthusiasts who worked to connect technology with business success. Our journey is a testament to the power of collaboration, creativity, and teamwork. Our main goal is to turn ideas into remarkable realities. From the initial spark to the finished product, we stand for using technology to help enterprises grow and thrive.</p>
        <p>This pillar focuses on <strong>Brand Narrative and Consistency</strong>. Your content should always reinforce your core values and mission, ensuring every post tells a cohesive story that builds trust and recognition with your audience.</p>
      </>
    ),
  },
  {
    title: 'Social strength',
    content: (
      <>
        <p>Social Strength is the pillar dedicated to <strong>Community Building and Networking</strong>. It's not just about broadcasting your message; it's about creating genuine connections. This involves actively engaging with comments, messages, and mentions to foster a loyal and interactive community.</p>
        <p>We help you identify key influencers and partners to collaborate with, expanding your network and leveraging trusted voices to amplify your brand's reach and authority.</p>
      </>
    ),
  },
  {
    title: 'Customer devotion',
    content: (
      <>
        <p>This pillar centers on <strong>Value Creation and Customer-Centricity</strong>. Every piece of content must provide value—whether it's by educating, entertaining, or solving a problem. Customer Devotion is built by making your audience feel heard and valued.</p>
        <p>We prioritize sharing user-generated content (UGC), testimonials, and running polls or Q&As. This practice not only provides social proof but also shows prospective clients that your brand genuinely cares about their experience and success.</p>
      </>
    ),
  },
  {
    title: 'Engagement Hike',
    content: (
      <>
        <p>The Engagement Hike pillar is focused on <strong>Optimizing Content for Interaction</strong>. This means going beyond simple 'likes' to generate meaningful comments, shares, and saves. We analyze trending content formats and tailor your posts to encourage a strong call-to-action (CTA).</p>
        <p>Tactics include creating interactive stories, initiating discussions on industry topics, and utilizing platform features like Reels, Carousels, and Live sessions to increase visibility and audience participation.</p>
      </>
    ),
  },
  {
    title: 'Audience monitoring',
    content: (
      <>
        <p>Audience Monitoring represents the <strong>Data-Driven Strategy and Listening</strong> component. This is where we track key performance indicators (KPIs), analyze demographic data, and most importantly, use social listening tools to understand market sentiment and competitor activities.</p>
        <p>The insights gathered from monitoring inform every other pillar, ensuring that your content is always relevant, your advertising is highly targeted, and your overall strategy is continuously refined for maximum return on investment (ROI).</p>
      </>
    ),
  },
];

const approachData = [
  {
    title: 'Unveiling Market Insights',
    description: 'We drive deep into industry trends, consumer behavior, and competitive landscapes to provide you with truly invisible insights. Our data-driven approach empowers you to make informed decisions, optimize campaigns, and stay ahead of the curve successfully.',
    icon: '📊' 
  },
  {
    title: 'Proactive Maintenance Solutions',
    description: 'Our team monitors your social platforms, ad systems, engagement, or software to stay in peak condition. By taking a proactive approach to upkeep, we minimize downtime, prevent costly repairs, and extend the lifespan of your assets, giving you peace of mind and sustained performance.',
    icon: '⚙️'
  },
  {
    title: 'Tailored Strategies for Your Needs',
    description: 'Our digital experts are skilled at designing and executing solutions that align perfectly with your unique goals and specialties. We work closely with you to understand your specific requirements, allowing us to design strategies that deliver measurable results, drive efficiency, and ensure success is not just a dream, but a reality.',
    icon: '🎯'
  },
  {
    title: 'Periodic Auditing Excellence',
    description: 'Our content strategies are meticulously tailored to meet your unique goals. By analyzing your target audience, competitors, and the latest platform changes, we ensure your messaging is always impactful, highly visible, and delivering the highest return on investment. We treat your content game with a strategic objective.',
    icon: '✅'
  },
  {
    title: 'Crafting Effective Content Strategies',
    description: 'Even on social media platforms, content creation is key. From engaging captions on Instagram to relevant hashtags on Twitter, we ensure your message is perfect. We focus on content that drives website traffic, boosts audience engagement, and most importantly, lead generation. We focus on each of them.',
    icon: '✍️'
  },
];

const processSteps = [
    { title: 'Study', description: 'Begin by learning about your business—what\'s special, who is who, and what others are doing. This helps us know where to begin.' },
    { title: 'Plan', description: 'After studying, we make a plan. We size your social media feeds, fix your profiles, and decide what to post and when.' },
    { title: 'Implement', description: 'This is where we execute. We take care of your social media accounts and post interesting things regularly. We also keep up with what\'s happening.' },
    { title: 'Assess', description: 'We track how things are going. If something doesn\'t work, we change it. We want to know what has had the best results to make your business shine online.' },
    { title: 'Revise', description: 'We let you know what\'s going on. Our team tells you what we\'re doing and how it\'s going. This helps us know where we\'re doing well and where we can improve. Our clients value this transparent process and active partnership.' },
];

const faqData = [
    {
        question: 'What is SRK Software\'s approach to social media marketing?',
        answer: 'Our approach is centered on "Building a Strong Foundation for Your Business." We start with Unveiling Market Insights, move to Tailored Strategies, execute with Proactive Maintenance, and maintain quality with Periodic Auditing and Crafting Effective Content Strategies. It is a full-cycle, data-driven approach.',
    },
    {
        question: 'How long does it take to see results from SMM?',
        answer: 'Results vary based on your industry and goals. Initial engagement and brand awareness can improve within 1-3 months. Significant measurable results, such as lead generation and sales, typically take 6-12 months of consistent, optimized strategy execution.',
    },
    {
        question: 'Which social media platforms do you specialize in?',
        answer: 'We specialize in a comprehensive suite of platforms including Facebook, Instagram, Twitter (X), Pinterest, and LinkedIn. Our strategies are customized for each platform to ensure your brand message is tailored to the specific audience and format of the network.',
    },
    {
        question: 'Do you handle both organic and paid social media campaigns?',
        answer: 'Yes, our services cover both organic content creation (posts, stories, community management) and paid advertising campaigns (ad strategy, targeting, creative development, and budget management) to maximize your reach and conversions.',
    },
    {
        question: 'How often do you report on campaign performance?',
        answer: 'We provide detailed monthly performance reports covering key metrics like engagement rate, reach, conversions, and ROI. We also schedule quarterly strategy review meetings to ensure we are aligned with your long-term business goals.',
    },
];

const SocialMediaMarketing = () => {
  const [activePillarIndex, setActivePillarIndex] = useState(0);
  const [openFAQIndex, setOpenFAQIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  return (
    <Home>
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          <HeroTitle>
            Get Ready To Shine Online And
            <BrandName>INTRODUCE YOUR BRAND</BrandName> To
            The <BrandName>DIGITAL WORLD</BrandName> Successfully!
          </HeroTitle>

          <HeaderContent>
            <IconContainer>
              <p>SOCIAL MEDIA GRAPHIC HERE</p>
            </IconContainer>

            <div>
              <p>"SRK Softwares" is positioned as the best social media marketing company in India, daily affecting similar services to those described in the previous text. However, without further details or context, it's challenging to provide a comprehensive analysis of the specific aspects of social media marketing for your business in Pune. Just let me know how I can assist you further!</p>
              <p>Our team of experts at <strong>SRK Software</strong> is dedicated to helping you expand your business reach and engage effectively with your target audience. As the best Social Media Marketing Services in Pune, India, we specialize in crafting and executing tailored social media strategies that align with your unique goals. Our results-driven approach means we work closely with you to achieve your objectives. We understand the power of social media in building relationships, generating leads, and driving sales. We can leverage our expertise to help you unlock the full potential of social media for your business success.</p>
            </div>

            <HeroImage>
              <p>COMPUTER GRAPHIC HERE</p>
            </HeroImage>
          </HeaderContent>
        </HeaderSection>
        
        {/* Why Opt Section */}
        <Section>
          <SectionTitle>Why opt for Social Media Marketing?</SectionTitle>
          <WhyOptGrid>
            <WhyOptItem>
              <IconContainer><p>👥</p></IconContainer>
              <WhyOptTitle>Expanded Audience Reach</WhyOptTitle>
              <WhyOptDescription>Reach millions of potential customers, granting you the chance to expand your network and connect with diverse demographics and markets.</WhyOptDescription>
            </WhyOptItem>
            <WhyOptItem>
              <IconContainer><p>🎯</p></IconContainer>
              <WhyOptTitle>Targeted Engagement</WhyOptTitle>
              <WhyOptDescription>With advanced targeting options, you can focus your efforts on the people who are most likely to convert, ensuring your message reaches the right audience at the right time.</WhyOptDescription>
            </WhyOptItem>
            <WhyOptItem>
              <IconContainer><p>👁️</p></IconContainer>
              <WhyOptTitle>Brand Visibility Increase</WhyOptTitle>
              <WhyOptDescription>Social media amplifies your brand's visibility, making it easier for people to discover your services, and values to a wider audience.</WhyOptDescription>
            </WhyOptItem>
            <WhyOptItem>
              <IconContainer><p>📈</p></IconContainer>
              <WhyOptTitle>Measurable Impact</WhyOptTitle>
              <WhyOptDescription>Social media platforms offers robust analytics that allow you to track performance, measure ROI, and give you to refine your strategy for optimum results.</WhyOptDescription>
            </WhyOptItem>
          </WhyOptGrid>
        </Section>

        {/* Pillars Section */}
        <PillarsSection>
          <PillarsTitle>The Pillars of Social Media Marketing</PillarsTitle>
          
          <PillarsNav>
            {pillarData.map((pillar, index) => (
              <PillarNavItem
                key={pillar.title}
                active={index === activePillarIndex}
                onClick={() => setActivePillarIndex(index)}
              >
                {pillar.title}
              </PillarNavItem>
            ))}
          </PillarsNav>
          
          <PillarContent>
            <PillarText>
              <SectionTitle dark>{pillarData[activePillarIndex].title}</SectionTitle>
              {pillarData[activePillarIndex].content}
            </PillarText>
            <PillarImage>
              <p>ROCKET GRAPHIC HERE</p>
            </PillarImage>
          </PillarContent>
        </PillarsSection>

        {/* CTA Section */}
        <CtaSection>
          <CtaTitle>Ready to talk about your project?</CtaTitle>
          <CtaSubtitle>Give us some details about you, and your project, and we'll be in touch.</CtaSubtitle>
          <CtaForm>
            <FormInput placeholder="What's your name?" />
            <FormInput placeholder="Your email address?" />
            <FormButton>Let's Get Started</FormButton>
          </CtaForm>
        </CtaSection>

        {/* Super SMM Services Bar */}
        <SMMBar>
          <SMMTitle>Our Super SMM Services</SMMTitle>
          <PlatformList>
            <PlatformItem><span className="icon facebook">f</span>FACEBOOK</PlatformItem>
            <PlatformItem><span className="icon instagram">📸</span>INSTAGRAM</PlatformItem>
            <PlatformItem><span className="icon twitter">X</span>TWITTER</PlatformItem>
            <PlatformItem><span className="icon pinterest">P</span>PINTEREST</PlatformItem>
            <PlatformItem><span className="icon linkedin">in</span>LINKEDIN</PlatformItem>
          </PlatformList>
          <SMMBarText>
            Connect with the world's largest social networks. We'll help you create engaging content, get targeted ads, and interact with your audience to build a strong presence for your business.
          </SMMBarText>
          <RuleText>"Rule the Socially-Inclined World with SRK Software"</RuleText>
        </SMMBar>

        {/* Our Approach Section */}
        <ApproachSection>
          <SectionTitle>Our Approach to <br />Building a Strong Foundation For Your Business</SectionTitle>
          <div>
            {approachData.map((item, index) => (
              <ApproachItem key={index}>
                <ApproachIcon>{item.icon}</ApproachIcon>
                <ApproachContent>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </ApproachContent>
              </ApproachItem>
            ))}
          </div>
        </ApproachSection>

        {/* The Superb Process Section */}
        <ProcessSection>
          <ProcessTitle>The Superb Process of</ProcessTitle>
          <ProcessSubtitle>Reshaping Brand's Social Perception</ProcessSubtitle>
          <ProcessGrid>
            <ProcessImageContainer>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.5rem' }}>PROCESS GRAPHIC HERE</p>
            </ProcessImageContainer>
            <ProcessStepList>
              {processSteps.map((step, index) => (
                <StepItem key={index} number={index + 1}>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </StepItem>
              ))}
            </ProcessStepList>
          </ProcessGrid>
        </ProcessSection>

        {/* Frequently Asked Questions */}
        <FAQSection>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <FAQContainer>
            {faqData.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion
                  active={index === openFAQIndex}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ToggleIcon active={index === openFAQIndex}>▼</ToggleIcon>
                </FAQQuestion>
                <FAQAnswer active={index === openFAQIndex}>
                  <p>{faq.answer}</p>
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </FAQSection>
      </PageContainer>
    </Home>
  );
};

export default SocialMediaMarketing;